import { useEffect, useId, useRef } from 'react';

import {
  useUnsavedChanges,
  type RegisterUnsavedChangesOptions,
} from '../../contexts/UnsavedChangesContext';

export interface UseRegisterUnsavedChangesOptions {
  scope: RegisterUnsavedChangesOptions['scope'];
  isDirty: boolean;
  onDiscard?: () => void;
  enabled?: boolean;
}

/**
 * Registers a dirty source with the global unsaved-changes guard.
 * Re-notifies the provider when `isDirty` changes so navigation blockers stay in sync.
 */
export function useRegisterUnsavedChanges({
  scope,
  isDirty,
  onDiscard,
  enabled = true,
}: UseRegisterUnsavedChangesOptions): void {
  const { register } = useUnsavedChanges();
  const isDirtyRef = useRef(isDirty);
  const onDiscardRef = useRef(onDiscard);

  const reactId = useId();
  const stableIdRef = useRef<symbol | null>(null);
  if (!stableIdRef.current) {
    stableIdRef.current = Symbol(`unsavedChanges-${reactId}`);
  }

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    onDiscardRef.current = onDiscard;
  }, [onDiscard]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    return register({
      id: stableIdRef.current!,
      isDirty: () => isDirtyRef.current,
      onDiscard: () => onDiscardRef.current?.(),
      scope,
    });
  }, [enabled, register, scope]);
}
